import styles from './Microsite.module.scss';
import {useCallback, useContext, useState} from "react";
import {StoreContext} from "../../../stores/StoreLoader";
import FontAwesome from "../../utilities/FontAwesome";
import classNames from 'classnames';
import {convertFlatToTree} from "../../../utils/DataUtilities";
import {IBreadcrumb} from "../../../pages/MainGridPage";
import {runInAction, toJS} from "mobx";
import {observer} from "mobx-react";
import NotificationManager from "../../notifications/NotificationManager";
import {MicrositeNavigation} from "./MicrositeNavigation";

type IMicrositeProps = {
    micrositeOpen: boolean,
    setMicrositeOpen: React.SetStateAction<any>,
}

export interface IMicrositeLink extends IBreadcrumb {
    path: string,
    id: string,
    children: IMicrositeLink[],
}

function getMicrositeEnabled(microsite) {
    if (!microsite) return false;
    const settings = microsite.json_data?.settings ? microsite.json_data?.settings : microsite.json_data?.block?.settings;
    if (settings && typeof settings.micrositeEnabled === "boolean") {
        return settings.micrositeEnabled;
    }
    return microsite.navigation.length > 2;
}

function getMicrositeEnabledJsonData(microsite, value) {
    const newJsonData = toJS(microsite.json_data);

    // classrooms and departments do not have the block object.
    if (newJsonData?.block) {
        newJsonData.block.settings = {
            ...newJsonData.block?.settings || {},
            micrositeEnabled: value,
        }
    } else {
        newJsonData.settings = {
            ...newJsonData?.settings || {},
            micrositeEnabled: value,
        };
    }

    return newJsonData;
}

export const useMicrosite = () => {
    const {organizationStore} = useContext(StoreContext);

    const microsite = organizationStore.currentOrganization.microsite;
    const micrositeEnabled: boolean = getMicrositeEnabled(microsite);

    const toggleShouldShowMicrosite = useCallback(async () => {
        try {
            const newJsonData = getMicrositeEnabledJsonData(microsite, !micrositeEnabled);
            const result = await organizationStore.fetchClient("organizations", "organizations_partial_update", {
                "id": microsite.id,
                data: {
                    json_data: newJsonData,
                },
            });
            runInAction(() => microsite.json_data = result.obj.json_data);
            NotificationManager.success(`Microsite successfully ${micrositeEnabled ? "disabled" : "enabled"}!`);
        } catch (e) {
            NotificationManager.error("Something went wrong. Please try again.")
        }
    }, [micrositeEnabled]);

    let subNavigation: IMicrositeLink[] = [];
    if (microsite) {
        subNavigation = convertFlatToTree(microsite.navigation.slice(1).map(link => ({
            ...link,
            id: link.model.id,
            path: link.model.path,
            grid_sort_order: link.model.grid_sort_order,
        })))
    }

    const shouldShowMicrosite = !!microsite && micrositeEnabled;

    return {
        microsite,
        canShowMicrosite: !!microsite,
        shouldShowMicrosite,
        toggleShouldShowMicrosite,
        subNavigation,
    }
}

export const Microsite = observer((props: IMicrositeProps) => {
    const {organizationStore, userStore, modalStore} = useContext(StoreContext);

    const [reorder, setReorder] = useState(false);

    const isTopLevelMicrositePage = organizationStore.currentOrganization.microsite.navigation[0]?.model.id === organizationStore.currentOrganization.id;

    const closeButtonContainerClassName = classNames({
        [styles.closeButtonContainer]: true,
        [styles.closeButtonContainerIsOpen]: props.micrositeOpen,
    })
    const reorderButtonClassName = classNames({
        [styles.reorderButton]: true,
        [styles.reorderButtonEnabled]: reorder,
    })

    const {subNavigation} = useMicrosite();

    return <div className={styles.container}>
        <div className={closeButtonContainerClassName}>
            <button onClick={() => props.setMicrositeOpen(false)}>
                <FontAwesome name={"fa-times"} prefix={"fas"} size={"2x"}/>
            </button>
        </div>
        {subNavigation.length > 0 && <ul className={styles.linksContainer}>
            {subNavigation.map((link, index) => <MicrositeNavigation
                key={link.id}
                link={link}
                index={index}
                reorder={reorder}
                micrositeOpen={props.micrositeOpen}
                setMicrositeOpen={props.setMicrositeOpen}
            />)}
        </ul>}
        {userStore.editor && isTopLevelMicrositePage && <div>
            <button onClick={() => {
                modalStore.addModal({
                    id: "addOrEditBlock",
                    type: 'addOrEditBlock',
                    intent: "add",
                    blockType: "section",
                })
            }
            }>Create Section
            </button>
            <button className={reorderButtonClassName} onClick={() => setReorder(v => !v)}>
                Reorder Sections
            </button>
        </div>}
    </div>
})
